import { useState, useEffect, useCallback } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { Tooltip, IconButton, Box } from "@mui/material";
import BagsHistoryModal from "./BagsHistoryModal";
import AddBagsModal from "./AddBagsModal";
import { pmndpAPI } from "../../../services/pmndp.api";
import { PMNDPTableSkeleton } from "../components/PMNDPLoader";

const LABELS = {
  title: "Peritoneal Dialysis Module",
  abhaNumber: "ABHA Number",
  abhaAddress: "ABHA Address",
  healthId: "Health ID",
  patientName: "Patient Name",
  mobile: "Mobile",
  mobileNo: "Mobile No.",
  sNo: "S.No.",
  age: "Age",
  gender: "Gender",
  lastIssueDate: "Last Issue Date",
  nextIssueDate: "Next Issue Date",
  action: "Action",
  search: "Search",
  reset: "Reset",
  page: "Page",
  of: "of",
  noData: "No Data Found",
};

const DEFAULT_SEARCH = {
  abhaNumber: "",
  abhaAddress: "",
  healthId: "",
  patientName: "",
  mobile: "",
};

export const PeritonealDialysisModule = () => {
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFields, setSearchFields] = useState(DEFAULT_SEARCH);
  const [openBagHistory, setOpenBagHistory] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const getPeritonealList = useCallback(
    async (pageNo = 1, search) => {
      try {
        setIsLoading(true);
        const resp = await pmndpAPI.getPeritonealDialysisList({
          page: pageNo,
          pageLength: pageSize,
          abhaId: search.abhaNumber,
          abha_address: search.abhaAddress,
          healthIdNumber: search.healthId,
          name: search.patientName,
          mobile: search.mobile,
        });
        const list = resp?.data?.data?.dataGet;
        const total = resp?.data?.data?.totalItems || 0;
        setTableData(Array.isArray(list) ? list : []);
        setTotalItems(total);
      } catch (error) {
        console.error("Error fetching peritoneal dialysis list:", error);
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    getPeritonealList(1, DEFAULT_SEARCH);
  }, [getPeritonealList]);

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    getPeritonealList(1, searchFields);
  };

  const handleReset = () => {
    setSearchFields(DEFAULT_SEARCH);
    setPage(1);
    getPeritonealList(1, DEFAULT_SEARCH);
  };

  const handlePageChange = (newPage) => {
    if (newPage !== page) {
      setPage(newPage);
      getPeritonealList(newPage, searchFields);
    }
  };

  return (
    <div>
      <div className="pmndp-card pmndp-fade-in">
        <div className="pmndp-header">
          <div className="pmndp-header-content">
            <div className="pmndp-header-left">
              <div className="pmndp-icon">
                <img src={"/images/PMNDP-logo.png"} alt="PMNDP" />
              </div>
              <h1 className="pmndp-title">{LABELS.title}</h1>
            </div>
          </div>
        </div>

        <div className="pmndp-filter-section">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", alignItems: "end" }}>
            {[
              { label: LABELS.abhaNumber, name: "abhaNumber", placeholder: "ABHA Number" },
              { label: LABELS.abhaAddress, name: "abhaAddress", placeholder: "ABHA Address" },
              { label: LABELS.healthId, name: "healthId", placeholder: "Health ID" },
              { label: LABELS.patientName, name: "patientName", placeholder: "Patient Name" },
              { label: LABELS.mobile, name: "mobile", placeholder: "Mobile" },
            ].map(({ label, name, placeholder }) => (
              <div key={name} className="pmndp-field" style={{ flex: "1 1 150px" }}>
                <label>{label}</label>
                <input
                  type="text"
                  name={name}
                  placeholder={placeholder}
                  value={searchFields[name]}
                  onChange={handleSearchChange}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: "12px" }}>
              <button className="pmndp-btn pmndp-btn-primary" type="button" onClick={handleSearch}>
                <i className="fa fa-search"></i> {LABELS.search}
              </button>
              <button className="pmndp-btn pmndp-btn-secondary" type="button" onClick={handleReset}>
                <i className="fa fa-refresh"></i> {LABELS.reset}
              </button>
            </div>
          </div>
        </div>

        <div className="pmndp-table-container" style={{ marginTop: "20px" }}>
          {isLoading ? (
            <PMNDPTableSkeleton rows={5} columns={8} />
          ) : (
            <div className="table-responsive">
              <table className="pmndp-table">
                <thead>
                  <tr>
                    <th>{LABELS.sNo}</th>
                    <th>{LABELS.patientName}</th>
                    <th>{LABELS.age}</th>
                    <th>{LABELS.gender}</th>
                    <th>{LABELS.mobileNo}</th>
                    <th>{LABELS.abhaNumber}</th>
                    <th>{LABELS.abhaAddress}</th>
                    <th>{LABELS.healthId}</th>
                    <th>{LABELS.lastIssueDate}</th>
                    <th>{LABELS.nextIssueDate}</th>
                    <th>{LABELS.action}</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(tableData) && tableData.length > 0 ? (
                    tableData.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>{(page - 1) * pageSize + index + 1}</td>
                        <td><strong>{item.patient_name || "NA"}</strong></td>
                        <td>{item.age || "NA"}</td>
                        <td>{item.gender || "NA"}</td>
                        <td>{item.mobile || "NA"}</td>
                        <td>{item.abha_number || "NA"}</td>
                        <td>{item.abha_address || "NA"}</td>
                        <td>{item.health_id_number || "NA"}</td>
                        <td>{item.last_issue_date || "NA"}</td>
                        <td>{item.next_issue_date || "NA"}</td>
                        <td>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="View Bag History" arrow>
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedPatient(item); setOpenBagHistory(true); }}
                                sx={{ backgroundColor: "#2c9faf", color: "#fff", borderRadius: "6px", width: 36, height: 36, "&:hover": { backgroundColor: "#238a98" } }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {!item.isdeleted && (item.status === 3 || item.status === 1) && (
                              <Tooltip title="Add Bag" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => { setSelectedPatient(item); setShowAddModal(true); }}
                                  sx={{ backgroundColor: "#1e73e8", color: "#fff", borderRadius: "6px", width: 36, height: 36, "&:hover": { backgroundColor: "#155ec4" } }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11">
                        <div className="pmndp-empty-state">
                          <div className="pmndp-empty-title">{LABELS.noData}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="pmndp-pagination">
              <button className="pmndp-pagination-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>‹</button>
              <span className="pmndp-pagination-info">{LABELS.page} {page} {LABELS.of} {totalPages}</span>
              <button className="pmndp-pagination-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>›</button>
            </div>
          )}

        </div>
      </div>

      <BagsHistoryModal
        isOpen={openBagHistory}
        onClose={() => setOpenBagHistory(false)}
        patient={selectedPatient}
      />
      <AddBagsModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        patient={selectedPatient}
        onSuccess={() => getPeritonealList(page, searchFields)}
      />

    </div>
  );
};

export default PeritonealDialysisModule;
